import { funnelApi, initialSessionId, reportClientError } from '../lib/api'

/**
 * Landing consent sequencing.
 *
 * Two different things are recorded here, and they are different in law:
 *
 *   1. **The agreement.** Accepting the Terms and Privacy Policy in order to use
 *      the funnel. Performance of a contract / legitimate interest — carried by
 *      the hero CTA ("by continuing, you agree"), which is a fair way to present
 *      terms because the user cannot proceed without them and nothing optional
 *      is smuggled in alongside.
 *
 *   2. **Research participation.** Letting the answers feed the Bonded Research
 *      Lab. This is consent under GDPR Art. 6(1)(a), and consent has to be
 *      *specific* and *freely given*: Art. 7(2) requires a request for consent
 *      to be "clearly distinguishable from the other matters", and EDPB
 *      Guidelines 05/2020 say bundling consent into acceptance of terms of use
 *      "defeats the specificity tenet". So it gets its own unticked control, and
 *      the funnel runs identically whether it is ticked or not.
 *
 * This file previously wrote research consent as an unconditional `true` chained
 * off the agreement, which made every legal document in the repo describing it
 * as a separate withdrawable opt-in untrue. See `docs/legal/README.md` §3 and
 * `docs/legal/internal/funnel-dpia-screening.md`.
 *
 * A decline is written as an explicit `false` rather than left null, so that
 * "asked and said no" is distinguishable from "never asked" — the same
 * reasoning the research lab already applies in `services/research.ts`. Without
 * that distinction we would have no way to prove we honoured a refusal.
 *
 * Blocking the tap on a round-trip would put the network between a tap and the
 * next screen, which is exactly what this funnel can't afford. Instead the tap
 * navigates immediately and every subsequent write chains off this promise.
 */
let pending: Promise<void> | null = null

/**
 * @param researchOptIn Whether the visitor affirmatively ticked the research
 *   control. `false` is recorded as a refusal, not skipped.
 */
export function commitLandingConsent(researchOptIn: boolean): Promise<void> {
	if (pending) return pending
	pending = (async () => {
		// Ordered: the API rejects every answer until the agreement is on record,
		// and the research decision belongs to a session that exists.
		await funnelApi.agreement(initialSessionId)
		await funnelApi.setResearch(initialSessionId, researchOptIn)
	})().catch((error) => {
		reportClientError('landing_consent', error)
		// Clear so a retry (e.g. the user goes back and picks again) can re-attempt
		// rather than chaining every later write onto a permanently failed promise.
		pending = null
		throw error
	})
	return pending
}

/** Resolves once consent is recorded, or immediately if it was never needed. */
export function consentSettled(): Promise<void> {
	return (pending ?? Promise.resolve()).catch(() => {})
}

/** Test seam — resets the module-level promise between runs. */
export function resetConsentForTests(): void {
	pending = null
}
